import React, { useMemo, useState } from 'react';
import moment, { Moment } from 'moment-timezone';

import { AverageDischargeChart } from '@components/chart/averageDischargeChart';
import { BatteryCentricService, IBatteryAverageDischargeData } from '../../../services/shared/battery/batteryCentricService';
import { useWorldAction } from '../../../lib/useWorldAction';
import { BatteryLevelChart } from './batteryLevelChart';
import { DataLoading } from '@components/loading/dataLoading';
import { useTranslation } from '@lib/useTypedTranslation';
import { getDisplayTimeZone } from '../../../lib/dateParams';
import './styles.css';


interface IProps {
  id: string,
  data: IBatteryAverageDischargeData[],
  isLoading?: boolean,
  /** IANA name of the battery's device time zone. Defaults to UTC when absent. */
  timeZone?: string
}

export function BatteryCharts(props: IProps) {
  const { t } = useTranslation('performance');
  const displayTimeZone = getDisplayTimeZone(props.timeZone);
  // The selected day is a local calendar date in the device's zone. `undefined` means "today in
  // that zone". It is only turned into a real UTC instant (the end of that local day) below, so
  // 23 and 25 hour days on DST transitions select the correct real range.
  const [selectedDayKey, setSelectedDayKey] = useState<string | undefined>(undefined);
  const batteryLevelDate = useMemo(
    () => (selectedDayKey ? moment.tz(selectedDayKey, displayTimeZone) : moment.utc().tz(displayTimeZone)).endOf('day').valueOf(),
    [selectedDayKey, displayTimeZone]
  );
  const title = t('AVERAGE_HOURLY_DISCHARGE_PAST_30DAYS');
  const service = new BatteryCentricService();

  const doGetSelectedTzDateBatteryLevel = useWorldAction(service.getSmartBatterySelectedTzDateBatteryLevel);
  async function loadDetails(): Promise<any> {
    return await doGetSelectedTzDateBatteryLevel(props.id, batteryLevelDate, displayTimeZone);
  }

  // The average discharge chart is bucketed by local calendar day, so a clicked bar's date names a local day
  const onSetDate = (date: string | Moment) => setSelectedDayKey(moment.utc(date).format('YYYY-MM-DD'));

  return (
    <div className="row details_information h-100">
      <div className="col-md-6 col-sm-12 col-12">
        <AverageDischargeChart
          data={props.data}
          title={title}
          className={"battery-average-discharge-chart"}
          setDate={onSetDate}
          isLoading={props?.isLoading}
          timeZone={displayTimeZone}
        />
      </div>
      <div className="col-md-6 col-sm-12 col-12">
        <DataLoading key={`${props.id}:${batteryLevelDate}:${displayTimeZone}`} loadData={loadDetails} transparentOverlay={true}>
          <BatteryLevelChart
            data={null}
            displayTimeZone={displayTimeZone}
            date={batteryLevelDate}
          />
        </DataLoading>
      </div>
    </div>
  );
}
